import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { CollectionAccess, CollectionUserActionEnum } from '@app/library/panes/my-library-collections/models';
import { CollectionsService, ContextService, FilesService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { LibraryRouter } from '@models/library.router';

import { Collection, ImageSizeEnum, User } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover, OnUiCoverModule } from 'onclass-ui';
import { takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-library-collections-mini',
  standalone: true,
  imports: [LocaleSharedModule, CommonModule, RouterModule, OnUiCoverModule],
  templateUrl: './library-collections-mini.component.html',
  styleUrls: ['./library-collections-mini.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LibraryCollectionsMiniComponent extends ChangableComponent implements OnInit {
  protected context = inject(ContextService);
  protected collectionsService = inject(CollectionsService);
  deviceService = inject(DeviceDetectorService);

  public collectionRoute: string;
  public editCollectionUrl: string;
  public noCover: OnUiCover = {
    form: 'base',
    type: 'cover',
  };
  public debug$ = this.context.debug$;

  @Input()
  public set collection(collection: Collection) {
    this._collection = collection;
    this.prepareCard();
  }

  public get collection(): Collection {
    return this._collection;
  }

  @Output() clicked = new EventEmitter<{ type: string; collection: Collection }>();
  @Output() openCollectionInModal = new EventEmitter<Collection>();

  public actions = CollectionUserActionEnum;

  private _me: User;
  private _collection: Collection;
  private _accessHelper: CollectionAccess;

  public ngOnInit(): void {
    this.context.me$.pipe(tap(), takeUntil(this.destroyed$)).subscribe({
      next: (me) => {
        this._me = me;
        this.prepareCard();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  public get isMobile() {
    return this.deviceService.isMobile();
  }

  public coverCssClasses(count: number, index: number): any {
    switch (count) {
      case 1:
        return { narrow: false, short: false };
      case 2:
        return { narrow: false, short: true };
      case 3:
        return index === 0 ? { narrow: false, short: true } : { narrow: true, short: true };
      case 4:
        return { narrow: true, short: true };
      default:
        return { narrow: false, short: false };
    }
  }

  public coverImg(id: string): OnUiCover {
    return {
      img: FilesService.getFileUrlBySize(id, ImageSizeEnum.sm),
      form: 'base',
      type: 'cover',
    };
  }

  public onArchiveCollection() {
    this.collectionsService
      .archive(this.collection.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.clicked.emit({ type: 'Archive', collection: this.collection });
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onUnPublishCollection() {
    this.collectionsService
      .unPublish(this.collection.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.clicked.emit({ type: 'Unpublish', collection: this.collection });
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onPublishCollection() {
    this.collectionsService
      .publish(this.collection.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.clicked.emit({ type: 'Publish', collection: this.collection });
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onRestoreCollection() {
    this.collectionsService
      .restore(this.collection.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.clicked.emit({ type: 'Restore', collection: this.collection });
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public publishStatusTitle(): string {
    return `publish-status.${this.collection?.publish_status}`;
  }

  public get cans() {
    return this._accessHelper?.can;
  }

  public can(action: CollectionUserActionEnum): boolean {
    return this._accessHelper?.can[action];
  }

  public onOpenCardInModal() {
    this.openCollectionInModal.emit(this._collection);
  }

  private prepareCard() {
    this._accessHelper = new CollectionAccess(this._me, this._collection);
    this.editCollectionUrl = '/content/collections/edit/' + this.collection.id;
    this.collectionRoute = this.collection?.slug
      ? LibraryRouter.collectionRouteUniversal(this.collection)
      : this.editCollectionUrl;
    this.detectChanges();
  }

  public trackByFn(index) {
    return index;
  }
}
